import React, { useMemo } from "react"
import PropTypes from "prop-types"
import Icon from "@common/display/Icon"
import UncontrolledTooltip from "@common/display/ToolTip/UncontrolledTooltip"

const HeatMapGraph = ({
  data,
  rowLabels,
  columnLabels,
  style,
  title,
  rowHeight,
  rowLabelWidth,
  minValue = 0,
  maxValue = 1,
  legendTooltip,
}) => {
  const tooltipId = useMemo(() => `heatmap-legend-tooltip-${Math.random().toString(36).substr(2, 9)}`, [])

  const getColor = (value) => {
    // Normalize value to 0-1 based on actual data range
    const range = maxValue - minValue
    const normalizedValue = range > 0 ? (value - minValue) / range : 0.5
    const clampedValue = Math.max(0, Math.min(1, normalizedValue))

    if (clampedValue <= 0.5) {
      const ratio = clampedValue * 2
      const r = 255
      const g = Math.round(255 * ratio)
      return `rgb(${r}, ${g}, 0)`
    } else {
      const ratio = (clampedValue - 0.5) * 2
      const r = Math.round(255 * (1 - ratio))
      const g = 255
      return `rgb(${r}, ${g}, 0)`
    }
  }

  const ROW_LABEL_WIDTH = rowLabelWidth || 60
  const ROW_HEIGHT = rowHeight || 20
  const CELL_GAP = 1
  const LABEL_HEIGHT = 24

  return (
    <div
      className="heat-map"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {/* Title */}
      {title && (
        <div
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            textAlign: "center",
            padding: "8px 0",
          }}
        >
          {title}
        </div>
      )}

      {/* Color legend */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          fontSize: "10px",
          padding: "4px 8px",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <span>Low</span>
        <div
          style={{
            width: 100,
            height: 12,
            background: "linear-gradient(to right, rgb(255,0,0), rgb(255,255,0), rgb(0,255,0))",
            borderRadius: 2,
          }}
        />
        <span>High</span>
        {legendTooltip && (
          <>
            <Icon icon="help_outline" id={tooltipId} style={{ fontSize: "14px", cursor: "help" }} />
            <UncontrolledTooltip target={tooltipId} placement="top">
              {legendTooltip}
            </UncontrolledTooltip>
          </>
        )}
      </div>

      {/* Column labels - sticky header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontSize: "10px",
          height: LABEL_HEIGHT,
          flexShrink: 0,
          marginBottom: 4,
          position: "sticky",
          top: 0,
          backgroundColor: "#1a1a2e",
          zIndex: 1,
        }}
      >
        <div style={{ width: ROW_LABEL_WIDTH, flexShrink: 0 }} />
        <div style={{ display: "flex", flex: 1, gap: CELL_GAP }}>
          {columnLabels.map((label, index) => (
            <div
              key={`col-${index}`}
              style={{
                flex: 1,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable rows container */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {data.map((row, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            style={{
              display: "flex",
              height: ROW_HEIGHT,
              marginBottom: CELL_GAP,
            }}
          >
            {/* Row label */}
            <div
              style={{
                width: ROW_LABEL_WIDTH,
                flexShrink: 0,
                fontSize: "10px",
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingRight: 4,
              }}
            >
              {rowLabels[rowIndex]}
            </div>

            {/* Heat map cells */}
            <div style={{ display: "flex", flex: 1, gap: CELL_GAP }}>
              {row.map((value, colIndex) => (
                <div
                  key={`cell-${rowIndex}-${colIndex}`}
                  style={{
                    flex: 1,
                    backgroundColor: getColor(value),
                    borderRadius: "1px",
                    cursor: "pointer",
                  }}
                  title={`${rowLabels[rowIndex]}: ${value?.toFixed(3) ?? "N/A"}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

HeatMapGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  rowLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  columnLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  colorRange: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
  rowHeight: PropTypes.number,
  rowLabelWidth: PropTypes.number,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  legendTooltip: PropTypes.string,
}

export default HeatMapGraph
